import { useCallback, useRef, useState } from 'react';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';
import { softwareApi, businessProcessApi, businessUnitApi, regulationApi, systemOwnerApi, vendorApi } from '../api/systemRegisterApi';
import { apiErrorMessage, unwrapList, unwrapRecords } from '../utils/responseHelpers';

const useSoftwareService = () => {
  const [items, setItems] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();
  const [lookups, setLookups] = useState({
    businessUnits: [],
    businessProcesses: [],
    systemOwners: [],
    vendors: [],
    regulations: [],
  });
  const lookupsPromiseRef = useRef(null);
  const lookupsLoadedRef = useRef(false);

  const loadLookups = useCallback(async ({ force = false } = {}) => {
    if (!force && lookupsLoadedRef.current && lookupsPromiseRef.current == null) {
      return { ok: true };
    }
    if (!force && lookupsPromiseRef.current) {
      return lookupsPromiseRef.current;
    }

    const active = { status: 'ACTIVE' };
    const request = (async () => {
      try {
        const [bu, bp, so, v, reg] = await Promise.all([
          businessUnitApi.list(active),
          businessProcessApi.list(active),
          systemOwnerApi.list(active),
          vendorApi.list(active),
          regulationApi.list(active),
        ]);
        const next = {
          businessUnits: unwrapList(bu),
          businessProcesses: unwrapList(bp),
          systemOwners: unwrapList(so),
          vendors: unwrapList(v),
          regulations: unwrapList(reg),
        };
        setLookups(next);
        lookupsLoadedRef.current = true;
        return { ok: true, lookups: next };
      } catch (error) {
        setLookups({
          businessUnits: [],
          businessProcesses: [],
          systemOwners: [],
          vendors: [],
          regulations: [],
        });
        lookupsLoadedRef.current = false;
        return { ok: false, error };
      } finally {
        lookupsPromiseRef.current = null;
      }
    })();

    lookupsPromiseRef.current = request;
    return request;
  }, []);

  const fetchItems = useCallback(
    async (page, size, search = '', showAll = false) => {
      toggleLoading();
      try {
        const params = { page, size, status: showAll ? 'ALL' : 'ACTIVE' };
        if (search?.trim()) params.search = search.trim();
        const response = await softwareApi.list(params);
        const { rows, total } = unwrapRecords(response);
        setItems(rows);
        setTotalRecords(total);
      } catch (error) {
        toast.error(apiErrorMessage(error, 'Failed to load software.'));
        setItems([]);
        setTotalRecords(0);
      } finally {
        toggleLoading();
      }
    },
    [toggleLoading]
  );

  const createItem = useCallback(async (payload) => {
    const response = await softwareApi.create(payload);
    return response.data?.data ?? response.data;
  }, []);

  const updateItem = useCallback(async (id, payload) => {
    const response = await softwareApi.update(id, payload);
    return response.data?.data ?? response.data;
  }, []);

  const removeItem = useCallback(async (id) => {
    await softwareApi.remove(id);
  }, []);

  const getItem = useCallback(async (id) => {
    const response = await softwareApi.get(id);
    return response.data?.data ?? response.data;
  }, []);

  return {
    items,
    totalRecords,
    isLoading,
    lookups,
    loadLookups,
    fetchItems,
    createItem,
    updateItem,
    removeItem,
    getItem,
  };
};

export default useSoftwareService;
